import { selectableLocationForMission } from '../content/locations';
import initialLayout from '../content/mapLayout.json';
import { missionCatalog, soldierMissionId } from '../content/mapCatalog';
import {
  MapLayoutDraft,
  draggedMapPosition,
  parseMapLayout,
  type MapLayout,
} from '../domain/map/MapLayout';
import {
  ProjectMapLayoutRepository,
  type MapLayoutRepository,
} from '../domain/map/MapLayoutRepository';

function escape(value: string): string {
  return value.replace(
    /[&<>"']/g,
    (character) =>
      ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[character] ??
      character,
  );
}

interface MapView {
  completedPuzzles: readonly string[];
  layout: MapLayout;
  editing: boolean;
  panel: string | null;
  canEdit: boolean;
  saving: boolean;
  notice: string;
}

export function missionMapMarkup(view: MapView): string {
  const placed = new Set(view.layout.markers.map((marker) => marker.missionId));
  const markers = view.layout.markers
    .map((marker) => {
      const mission = missionCatalog.find((entry) => entry.id === marker.missionId);
      if (!mission) return '';
      return `<button class="map-mission-marker ${selectableLocationForMission(mission.id, view.completedPuzzles) ? 'is-playable' : ''}"
      style="left:${marker.x * 100}%;top:${marker.y * 100}%"
      data-mission-marker="${escape(mission.id)}" aria-label="${escape(mission.title)}${view.editing ? ' – flyt med musen eller piletasterne' : ''}"
      ${view.saving ? 'disabled' : ''}>
      <span class="map-mission-pin" aria-hidden="true">●</span>
      <span class="map-mission-label">${escape(mission.title)}</span>
    </button>`;
    })
    .join('');
  const entries =
    view.panel === 'all' ? missionCatalog : missionCatalog.filter((m) => m.id === view.panel);
  const panel =
    view.panel === null
      ? ''
      : `<section class="modal-backdrop"><div class="mission-catalog-panel paper-panel" role="dialog" aria-modal="true" aria-labelledby="mission-catalog-title">
    <div class="panel-heading"><div><p class="eyebrow">${entries.length} ${entries.length === 1 ? 'historisk opgave' : 'historiske opgaver'}</p><h2 id="mission-catalog-title">${view.panel === 'all' ? 'Alle opgaver' : escape(entries[0]?.title ?? 'Opgave')}</h2></div>
    <button data-action="close-map-catalog" aria-label="Luk opgavelisten">✕</button></div>
    <p>${view.editing ? 'Klik på ⊕ for at tilføje en opgave. Flyt markøren på kortet, og klik Gem.' : 'Udforsk historierne. Slå Redigér kort til på kortet for at tilføje opgaver.'}</p>
    <div class="mission-catalog-list">${entries
      .map(
        (mission) => `<div class="mission-catalog-row">
      <details class="mission-catalog-entry" ${view.panel !== 'all' ? 'open' : ''}>
        <summary>${escape(mission.title)}<span>${selectableLocationForMission(mission.id, view.completedPuzzles) ? 'Spil nu' : 'Kan ikke vælges lige nu'}</span></summary>
        <div class="mission-catalog-description"><p>${escape(mission.description || 'Historien er på vej.')}</p>
        ${mission.place ? `<p class="mission-place">${escape(mission.place)}${mission.address ? ` · ${escape(mission.address)}` : ''}</p>` : ''}
        ${selectableLocationForMission(mission.id, view.completedPuzzles) ? `<button data-play-mission="${escape(mission.id)}" ${view.editing ? 'disabled' : ''}>Spil ${escape(mission.title)}</button>${view.editing ? '<p>Gem eller annuller kortredigeringen, før du spiller.</p>' : ''}` : '<p class="mission-coming-soon">Kan ikke vælges lige nu.</p>'}
        </div>
      </details>
      <div class="mission-map-actions">
        ${
          placed.has(mission.id)
            ? `<button data-locate-mission="${escape(mission.id)}" title="Vis på kortet" aria-label="Vis ${escape(mission.title)} på kortet">✓<span>På kortet</span></button>`
            : `<button data-add-mission="${escape(mission.id)}" ${!view.canEdit || !view.editing || view.saving ? 'disabled' : ''} title="${view.canEdit && view.editing ? 'Tilføj til kortet' : 'Slå Redigér kort til først'}" aria-label="Tilføj ${escape(mission.title)} til kortet">⊕<span>Tilføj</span></button>`
        }
        ${view.editing && placed.has(mission.id) && mission.id !== soldierMissionId ? `<button data-remove-mission="${escape(mission.id)}" aria-label="Fjern ${escape(mission.title)} fra kortet" ${view.saving ? 'disabled' : ''}>Fjern</button>` : ''}
      </div>
    </div>`,
      )
      .join('')}</div>
  </div></section>`;
  return `<div class="map-mission-layer ${view.editing ? 'is-editing' : ''}" aria-label="Opgaver på kortet">
    ${markers}
    <div class="map-toolbar">
      <div class="map-toolbar-buttons">
        <button data-action="open-map-catalog" aria-haspopup="dialog">Alle ${missionCatalog.length} opgaver</button>
        ${
          view.editing
            ? `<strong class="map-edit-label">Redigering</strong><button class="primary-button" data-action="save-map" ${view.saving ? 'disabled' : ''}>${view.saving ? 'Gemmer …' : 'Gem'}</button><button data-action="cancel-map-edit" ${view.saving ? 'disabled' : ''}>Annuller</button>`
            : `<button data-action="edit-map" ${view.canEdit ? '' : 'disabled'} title="${view.canEdit ? 'Flyt opgavemarkører' : 'Åbn den lokale udviklingsversion for at redigere kortet'}">✎ Redigér kort</button>`
        }
      </div>
      <p class="map-edit-message" role="status">${escape(view.notice || (view.editing ? 'Træk markørerne på plads. Piletaster flytter den valgte markør. Klik Gem, når du er færdig.' : ''))}</p>
    </div>
  </div>${panel}`;
}

export class MissionMap {
  readonly #listeners = new AbortController();
  readonly draft = new MapLayoutDraft(parseMapLayout(initialLayout));
  #panel: string | null = null;
  #returnFocus: string | null = null;
  #revision = '';
  #ready = false;
  #saving = false;
  #notice = '';
  #drag: {
    id: string;
    pointerId: number;
    startX: number;
    startY: number;
    x: number;
    y: number;
    width: number;
    height: number;
  } | null = null;

  constructor(
    private readonly root: HTMLElement,
    private readonly render: () => void,
    private readonly playMission: (missionId: string) => void,
    private readonly getCompletedPuzzles: () => readonly string[],
    private readonly repository: MapLayoutRepository | null = import.meta.env.DEV
      ? new ProjectMapLayoutRepository()
      : null,
  ) {
    root.addEventListener('pointerdown', (event) => this.#pointerDown(event), {
      signal: this.#listeners.signal,
    });
    root.addEventListener('pointermove', (event) => this.#pointerMove(event), {
      signal: this.#listeners.signal,
    });
    root.addEventListener('pointerup', (event) => this.#pointerEnd(event, false), {
      signal: this.#listeners.signal,
    });
    root.addEventListener('pointercancel', (event) => this.#pointerEnd(event, true), {
      signal: this.#listeners.signal,
    });
    root.addEventListener('lostpointercapture', (event) => this.#pointerEnd(event, true), {
      signal: this.#listeners.signal,
    });
    window.addEventListener(
      'beforeunload',
      (event) => {
        if (!this.draft.editing) return;
        event.preventDefault();
        event.returnValue = '';
      },
      { signal: this.#listeners.signal },
    );
  }

  dispose(): void {
    this.#listeners.abort();
  }

  async load(): Promise<void> {
    if (!this.repository) return;
    try {
      const stored = await this.repository.load();
      this.draft.accept(stored.layout);
      this.#revision = stored.revision;
      this.#ready = true;
    } catch {
      this.#notice =
        'Kortredigering kunne ikke indlæses. Kontrollér udviklingsserveren, og genindlæs siden.';
    }
    this.render();
  }

  get inputBlocked(): boolean {
    return this.#panel !== null || this.draft.editing;
  }

  markup(): string {
    return missionMapMarkup({
      layout: this.draft.current,
      completedPuzzles: this.getCompletedPuzzles(),
      editing: this.draft.editing,
      panel: this.#panel,
      canEdit: this.#ready,
      saving: this.#saving,
      notice: this.#notice,
    });
  }

  closePanel(restoreFocus = false): void {
    const wasOpen = this.#panel !== null;
    this.#panel = null;
    if (restoreFocus && wasOpen) {
      this.render();
      this.#focusMarker(this.#returnFocus);
    }
  }

  bind(): void {
    const action = (name: string, callback: () => void) =>
      this.root.querySelector(`[data-action="${name}"]`)?.addEventListener('click', callback);
    action('open-map-catalog', () => this.#openPanel('all'));
    action('close-map-catalog', () => this.closePanel(true));
    action('edit-map', () => {
      if (!this.#ready) return;
      this.draft.begin();
      this.#notice = '';
      this.render();
      this.root.querySelector<HTMLButtonElement>('[data-action="save-map"]')?.focus();
    });
    action('cancel-map-edit', () => {
      if (this.#saving) return;
      this.draft.cancel();
      this.#notice = 'Ændringerne er annulleret.';
      this.render();
      this.root.querySelector<HTMLButtonElement>('[data-action="edit-map"]')?.focus();
    });
    action('save-map', () => {
      void this.#save();
    });
    this.root.querySelectorAll<HTMLButtonElement>('[data-play-mission]').forEach((button) =>
      button.addEventListener('click', () => {
        const id = button.dataset.playMission ?? '';
        if (!this.draft.editing && selectableLocationForMission(id, this.getCompletedPuzzles())) {
          this.closePanel();
          this.playMission(id);
        }
      }),
    );
    this.root.querySelectorAll<HTMLButtonElement>('[data-mission-marker]').forEach((button) => {
      const id = button.dataset.missionMarker ?? '';
      button.addEventListener('click', () => {
        if (this.draft.editing) return;
        this.root.querySelectorAll('.map-marker-message').forEach((message) => {
          message.parentElement?.classList.remove('has-message');
          message.remove();
        });
        if (selectableLocationForMission(id, this.getCompletedPuzzles())) this.playMission(id);
        else {
          const marker = this.draft.current.markers.find((entry) => entry.missionId === id);
          const message = document.createElement('span');
          message.className = `map-marker-message ${(marker?.x ?? 0) > 0.7 ? 'opens-left' : ''}`;
          message.setAttribute('role', 'status');
          message.textContent = 'Kan ikke vælges lige nu.';
          button.classList.add('has-message');
          button.append(message);
          window.addEventListener(
            'pointermove',
            () => {
              message.remove();
              button.classList.remove('has-message');
            },
            { once: true, signal: this.#listeners.signal },
          );
        }
      });
      button.addEventListener('keydown', (event) => {
        if (!this.draft.editing || this.#saving) return;
        const steps: Record<string, { x: number; y: number }> = {
          ArrowLeft: { x: -1, y: 0 },
          ArrowRight: { x: 1, y: 0 },
          ArrowUp: { x: 0, y: -1 },
          ArrowDown: { x: 0, y: 1 },
        };
        const step = steps[event.key];
        const marker = this.draft.current.markers.find((entry) => entry.missionId === id);
        if (!step || !marker) return;
        event.preventDefault();
        const distance = event.shiftKey ? 0.02 : 0.005;
        this.draft.move(id, { x: marker.x + step.x * distance, y: marker.y + step.y * distance });
        this.#updateMarker(id);
      });
    });
    this.root.querySelectorAll<HTMLButtonElement>('[data-add-mission]').forEach((button) =>
      button.addEventListener('click', () => {
        if (!this.#ready || !this.draft.editing || this.#saving) return;
        const id = button.dataset.addMission ?? '';
        this.draft.add(id);
        this.#panel = null;
        this.#notice = 'Opgaven er tilføjet. Træk markøren på plads, og klik Gem.';
        this.render();
        this.#focusMarker(id);
      }),
    );
    this.root.querySelectorAll<HTMLButtonElement>('[data-locate-mission]').forEach((button) =>
      button.addEventListener('click', () => {
        this.#panel = null;
        this.render();
        this.#focusMarker(button.dataset.locateMission ?? null);
      }),
    );
    this.root.querySelectorAll<HTMLButtonElement>('[data-remove-mission]').forEach((button) =>
      button.addEventListener('click', () => {
        if (this.#saving) return;
        const id = button.dataset.removeMission ?? '';
        this.draft.remove(id);
        this.render();
        const next = [...this.root.querySelectorAll<HTMLButtonElement>('[data-add-mission]')].find(
          (entry) => entry.dataset.addMission === id,
        );
        next?.focus();
      }),
    );
    this.root
      .querySelector<HTMLElement>('.mission-catalog-panel')
      ?.addEventListener('keydown', (event) => {
        if (event.key !== 'Tab') return;
        const panel = event.currentTarget as HTMLElement;
        const focusable = [
          ...panel.querySelectorAll<HTMLElement>('button:not(:disabled), summary'),
        ].filter((element) => element.getClientRects().length > 0);
        const first = focusable[0];
        const last = focusable.at(-1);
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last?.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first?.focus();
        }
      });
  }

  #openPanel(id: string): void {
    this.#panel = id;
    this.#returnFocus = id === 'all' ? null : id;
    this.render();
    this.root.querySelector<HTMLButtonElement>('[data-action="close-map-catalog"]')?.focus();
  }

  #focusMarker(id: string | null): void {
    const marker = [...this.root.querySelectorAll<HTMLButtonElement>('[data-mission-marker]')].find(
      (button) => button.dataset.missionMarker === id,
    );
    (
      marker ?? this.root.querySelector<HTMLButtonElement>('[data-action="open-map-catalog"]')
    )?.focus();
  }

  #updateMarker(id: string): void {
    const marker = this.draft.current.markers.find((entry) => entry.missionId === id);
    const element = [...this.root.querySelectorAll<HTMLElement>('[data-mission-marker]')].find(
      (entry) => entry.dataset.missionMarker === id,
    );
    if (marker && element) {
      element.style.left = `${marker.x * 100}%`;
      element.style.top = `${marker.y * 100}%`;
    }
  }

  #pointerDown(event: PointerEvent): void {
    if (
      !this.draft.editing ||
      this.#saving ||
      this.#panel !== null ||
      this.#drag ||
      event.button !== 0
    )
      return;
    const button =
      event.target instanceof Element
        ? event.target.closest<HTMLElement>('[data-mission-marker]')
        : null;
    const layer = this.root.querySelector('.map-mission-layer');
    const marker = this.draft.current.markers.find(
      (entry) => entry.missionId === button?.dataset.missionMarker,
    );
    if (!button || !layer || !marker) return;
    const bounds = layer.getBoundingClientRect();
    event.preventDefault();
    button.focus();
    this.#drag = {
      id: marker.missionId,
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      x: marker.x,
      y: marker.y,
      width: bounds.width,
      height: bounds.height,
    };
    this.root.setPointerCapture(event.pointerId);
    button.classList.add('is-dragging');
  }

  #pointerMove(event: PointerEvent): void {
    const drag = this.#drag;
    if (!drag || drag.pointerId !== event.pointerId) return;
    this.draft.move(
      drag.id,
      draggedMapPosition(
        drag,
        { x: event.clientX - drag.startX, y: event.clientY - drag.startY },
        drag,
      ),
    );
    this.#updateMarker(drag.id);
  }

  #pointerEnd(event: PointerEvent, cancelled: boolean): void {
    const drag = this.#drag;
    if (!drag || drag.pointerId !== event.pointerId) return;
    if (cancelled) this.draft.move(drag.id, drag);
    else this.#pointerMove(event);
    this.#drag = null;
    if (this.root.hasPointerCapture(event.pointerId))
      this.root.releasePointerCapture(event.pointerId);
    this.render();
    this.#focusMarker(drag.id);
  }

  async #save(): Promise<void> {
    if (!this.repository || !this.draft.editing || this.#saving) return;
    this.#saving = true;
    this.#notice = '';
    this.render();
    try {
      const result = await this.repository.save(
        structuredClone(this.draft.current),
        this.#revision,
      );
      this.draft.accept(result.layout);
      this.#revision = result.revision;
      this.#notice = 'Kortet er gemt i projektet.';
    } catch (error) {
      this.#notice =
        error instanceof Error ? error.message : 'Kortet kunne ikke gemmes. Prøv igen.';
    } finally {
      this.#saving = false;
      this.render();
      this.root
        .querySelector<HTMLButtonElement>(
          `[data-action="${this.draft.editing ? 'save-map' : 'edit-map'}"]`,
        )
        ?.focus();
    }
  }
}
